import { differenceInDays, parseISO } from 'date-fns'
import deepEqual from 'fast-deep-equal'
import * as qs from 'qs'
import { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import {
  maxSelectableDays,
  rowsPerPageOptions,
} from 'src/features/monitoringDashboardAlpha/hooks/useMonitoringFilters'
import { ReportStatusGroupKey } from 'src/features/monitoringDashboardAlpha/types'
import { isValidDateString } from 'src/util/datetime/isValidDateString'
import { removeMatchingKeysAndValues } from 'src/util/removeMatchingKeysAndValues'

const statusGroupKeys: ReportStatusGroupKey[] = [
  'submitted',
  'notImplemented',
  'duringPeriod',
  'notStarted',
]

const defaultLimit = 25

/**
 * モニタリングダッシュボード詳細（L2）の絞り込み・ページネーション条件
 *
 * L1 から日付範囲・現場・ステータスを引き継ぎ、L2 ではスケジュール絞り込みを追加する。
 */
export type DetailFilters = {
  /** 開始日と終了日（ISO8601 拡張形式の日付 YYYY-MM-DD） */
  dateRange: [string, string] | null
  /** 引き継いだ現場（空配列なら全現場） */
  placeNodeUuids: string[]
  /** 引き継いだステータスグループ（null なら全ステータス） */
  status: ReportStatusGroupKey | null
  /** L2 で指定するスケジュール（空配列なら全スケジュール） */
  scheduleUuids: string[]
  offset: number
  limit: number
}

const initialFilters: DetailFilters = {
  dateRange: null,
  placeNodeUuids: [],
  status: null,
  scheduleUuids: [],
  offset: 0,
  limit: defaultLimit,
}

const qsOptions = {
  // arrayLimit のデフォルト(20)だと 21要素以上の配列が qs.parse でオブジェクト化され、
  // sanitize で空配列に化けて絞り込みが解除されるため上限を引き上げる
  parse: { ignoreQueryPrefix: true, arrayLimit: 1000 },
  stringify: { skipNulls: true, encode: false },
}

const sanitize = {
  // L1(useMonitoringFilters)と同等に、不正な期間は既定値へフォールバックする
  dateRange: (arg: unknown): DetailFilters['dateRange'] => {
    const [arg1, arg2] = Array.isArray(arg) ? arg : []
    if (!isValidDateString(arg1) || !isValidDateString(arg2)) {
      return initialFilters.dateRange
    }
    const dateFrom = parseISO(arg1)
    const dateTo = parseISO(arg2)
    if (
      // 日付が未来であってはならない
      dateFrom > new Date() ||
      dateTo > new Date() ||
      // 開始日が終了日より後の日付であってはならない
      dateFrom > dateTo ||
      // 日付の差が最大選択可能日数を超えてはならない
      differenceInDays(dateTo, dateFrom) >= maxSelectableDays
    ) {
      return initialFilters.dateRange
    }
    return [arg1, arg2]
  },
  stringArray: (arg: unknown): string[] =>
    Array.isArray(arg)
      ? arg.filter((v): v is string => typeof v === 'string')
      : [],
  status: (arg: unknown): ReportStatusGroupKey | null =>
    statusGroupKeys.includes(arg as ReportStatusGroupKey)
      ? (arg as ReportStatusGroupKey)
      : null,
  offset: (arg: unknown): number => {
    const n = Number(arg)
    return Number.isInteger(n) && n >= 0 ? n : initialFilters.offset
  },
  limit: (arg: unknown): number => {
    const n = Number(arg)
    return rowsPerPageOptions.includes(n) ? n : initialFilters.limit
  },
}

export const useDetailFilters = () => {
  const history = useHistory()
  const location = useLocation()

  const getFiltersFromQueryString = (): DetailFilters => {
    const raw = qs.parse(location.search, qsOptions.parse)
    return {
      dateRange: sanitize.dateRange(raw['dateRange']),
      placeNodeUuids: sanitize.stringArray(raw['placeNodeUuids']),
      status: sanitize.status(raw['status']),
      scheduleUuids: sanitize.stringArray(raw['scheduleUuids']),
      offset: sanitize.offset(raw['offset']),
      limit: sanitize.limit(raw['limit']),
    }
  }

  const [filters, setFilters] = useState<DetailFilters>(() =>
    getFiltersFromQueryString(),
  )

  useEffect(() => {
    const filtersToSet = removeMatchingKeysAndValues(filters, initialFilters)
    const newQueryString = qs.stringify(filtersToSet, qsOptions.stringify)
    const isAlreadySynchronized =
      newQueryString === history.location.search.substring(1)
    if (isAlreadySynchronized) return
    // L2 内のページネーション・絞り込み変更は履歴を積まずに置き換える。
    // こうすることで L2 は履歴上1エントリに集約され、「戻る」が前の L2 ページではなく
    // 常に L1 へ戻る（戻るボタン = L1↔L2 の行き来）。
    history.replace({ search: newQueryString })
  }, [filters, history])

  useEffect(
    () => {
      const newFilters = getFiltersFromQueryString()
      if (deepEqual(newFilters, filters)) return
      // eslint-disable-next-line react-hooks/set-state-in-effect -- useMonitoringFilters と同様の既存設計を踏襲
      setFilters(newFilters)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.search],
  )

  const setFilterOf = {
    // スケジュール変更時はページネーションを先頭に戻す
    scheduleUuids: (scheduleUuids: string[]) => {
      setFilters(prev => ({
        ...prev,
        scheduleUuids: sanitize.stringArray(scheduleUuids),
        offset: 0,
      }))
    },
    offset: (offset: number) => {
      setFilters(prev => ({ ...prev, offset: sanitize.offset(offset) }))
    },
    limit: (limit: number) => {
      setFilters(prev => ({ ...prev, limit: sanitize.limit(limit), offset: 0 }))
    },
  }

  return { filters, setFilterOf }
}
